import { sortBy, uniqBy } from 'lodash';
import moment from 'moment-timezone';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router';
import { useScheduledUpdate } from '../../misc/hooks';
import { DATE_FORMAT_URL, TIME_FORMAT_USER } from '../constants/datetime';
import { isCurrentMeeting } from '../filters/meetingFilters';
import { eventMomentFactory } from '../misc/eventMomentFactory';
import { useEvent } from './eventHooks';
import { useEventSchedule } from './schedule/useEventSchedule';
import { useMeetings } from './meeting/useMeetings';
import { useUpcomingMeetings } from './meeting/useUpcomingMeetings';
import { isCurrentSession } from '../filters/schedule/isCurrentSession';
import { isUpcomingSession } from '../filters/scheduleFilters';

//
// Format time and dates
//

export function formatEventTime(event, timestamp, format = TIME_FORMAT_USER) {
    return eventMomentFactory(event, timestamp).format(format);
}

export function useTimeFormatter(format = TIME_FORMAT_USER) {
    const { eventId } = useParams(),
        event = useEvent(eventId);
    return useCallback((timestamp) => formatEventTime(event, timestamp, format), [event.show_in_user_timezone, format]);
}

export function formatEventURLDate(event, timestamp) {
    return eventMomentFactory(event, timestamp).format(DATE_FORMAT_URL);
}

export function useDateURLFormatter() {
    const { eventId } = useParams(),
        event = useEvent(eventId);
    return useCallback((timestamp) => formatEventURLDate(event, timestamp), [event.show_in_user_timezone]);
}

/**
 * Makes sure the component updates each time a new session or meeting starts
 * @param {Number} eventId      Event ID
 * @param {Number} offsetValue  Offset value
 * @param {string} offsetUnit   Offset unit (moment units)
 */
export function useUpdateWhenScheduleAdvances(eventId, offsetValue = 0, offsetUnit = 'minute') {
    const nextProgramSession = useEventSchedule(eventId, {
            count: 1,
            filter: isUpcomingSession,
        })[0],
        nextMeeting = useUpcomingMeetings(eventId)[0],
        currentProgramSessions = useEventSchedule(eventId, {
            filter: isCurrentSession,
        }),
        currentMeetings = useMeetings(eventId, {
            filter: isCurrentMeeting,
        }),
        next = [nextProgramSession?.start_local, nextMeeting?.start_local, ...currentProgramSessions.map((s) => s.end_local), ...currentMeetings.map((m) => m.end_local)].filter((i) => i).sort()[0];

    useScheduledUpdate(next);
}

/**
 *
 * @param {string|moment} timestamp Timestamp for the time comparison
 * @returns {
 *      diff,       // Todal diff in seconds
 *      days,       // Total diff between dates
 *      hours,      // Number of hours, rounded down
 *      minutes,    // Remaining minutes, rounded down
 *      seconds     // Remaining seconds
 * }
 */
export function useCountdownTo(timestamp) {
    let diff = -moment().diff(timestamp, 'seconds'),
        days = -moment().startOf('day').diff(moment(timestamp).startOf('day'), 'days'),
        seconds = diff,
        minutes,
        hours;

    hours = Math.floor(seconds / 3600);
    seconds -= hours * 3600;
    minutes = Math.floor(seconds / 60);
    seconds -= minutes * 60;

    return { diff, days, hours, minutes, seconds };
}





